<script setup>
import {
  ref,
  computed,
  onMounted,
  onActivated,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
// NOOVI: CSV import (Gap FU-E — fase-11)
import FollowUpImportModal from './FollowUpImportModal.vue';

const store = useStore();
const router = useRouter();
const { t, locale } = useI18n();
const { accountScopedRoute } = useAccount();
const { checkPermissions } = usePolicy();
const isAdmin = computed(() => checkPermissions(['administrator']));

const isLoading = ref(true);
const activeView = ref('pending'); // 'pending' | 'history'
const selectedInbox = ref('all');
const isInboxDropdownOpen = ref(false);
const dateFilter = ref('all'); // 'all' | 'today' | 'week' | 'month'
const searchQuery = ref('');
const showSearchInput = ref(false);
const deleteConfirmationRef = ref(null);
const messageToDelete = ref(null);
const deletingIds = ref(new Set());
const retryingIds = ref(new Set());
const expandedConversations = ref(new Set());

// NOOVI: CSV import state (Gap FU-E — fase-11)
const showImportModal = ref(false);
const onImported = () => store.dispatch('followUps/fetchAll');

// Edit state
const showEditModal = ref(false);
const messageToEdit = ref(null);
const isUpdating = ref(false);
const editForm = ref({
  title: '',
  message: '',
  scheduled_at: '',
  follow_up_template_id: null,
});

// Templates
const templates = computed(
  () => store.getters['followUpTemplates/getTemplates'] || []
);

const allFollowUps = computed(() => {
  return store.getters['followUps/getAllFollowUps'] || [];
});

// Unique inboxes from data
const availableInboxes = computed(() => {
  const inboxMap = new Map();
  allFollowUps.value.forEach(msg => {
    if (msg.inbox_id && msg.inbox_name) {
      inboxMap.set(msg.inbox_id, msg.inbox_name);
    }
  });
  return Array.from(inboxMap.entries()).map(([id, name]) => ({ id, name }));
});

// Base filter: inbox + date + search
const baseFiltered = computed(() => {
  let messages = allFollowUps.value;

  // Inbox filter
  if (selectedInbox.value !== 'all') {
    const inboxId = parseInt(selectedInbox.value, 10);
    messages = messages.filter(msg => msg.inbox_id === inboxId);
  }

  // Date filter (applies to scheduled_at for pending, created_at for history)
  if (dateFilter.value !== 'all') {
    const now = new Date();
    const startOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    let cutoff;
    if (dateFilter.value === 'today') {
      cutoff = startOfDay;
    } else if (dateFilter.value === 'week') {
      cutoff = new Date(startOfDay.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (dateFilter.value === 'month') {
      cutoff = new Date(startOfDay.getTime() - 30 * 24 * 60 * 60 * 1000);
    }
    if (cutoff) {
      messages = messages.filter(msg => {
        const ts = (msg.scheduled_at || msg.created_at) * 1000;
        return ts >= cutoff.getTime();
      });
    }
  }

  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    messages = messages.filter(
      msg =>
        msg.title?.toLowerCase().includes(query) ||
        msg.message?.toLowerCase().includes(query) ||
        msg.conversation_display?.toLowerCase().includes(query) ||
        msg.inbox_name?.toLowerCase().includes(query)
    );
  }

  return messages;
});

// Pending: only pending status, sorted chronologically
const pendingMessages = computed(() => {
  return baseFiltered.value
    .filter(msg => msg.status === 'pending')
    .sort((a, b) => a.scheduled_at - b.scheduled_at);
});

// Pending grouped by time period (Hoje / Esta Semana / Mais Tarde)
const pendingGrouped = computed(() => {
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const startOfNextWeek = new Date(
    startOfToday.getTime() + 7 * 24 * 60 * 60 * 1000
  );

  const groups = {
    overdue: {
      label: t('FOLLOW_UPS.GROUPS.OVERDUE'),
      items: [],
      key: 'overdue',
    },
    today: {
      label: t('FOLLOW_UPS.GROUPS.TODAY'),
      items: [],
      key: 'today',
    },
    week: {
      label: t('FOLLOW_UPS.GROUPS.THIS_WEEK'),
      items: [],
      key: 'week',
    },
    later: {
      label: t('FOLLOW_UPS.GROUPS.LATER'),
      items: [],
      key: 'later',
    },
  };

  pendingMessages.value.forEach(msg => {
    const scheduledDate = new Date(msg.scheduled_at * 1000);
    if (scheduledDate < startOfToday) {
      groups.overdue.items.push(msg);
    } else if (
      scheduledDate < new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000)
    ) {
      groups.today.items.push(msg);
    } else if (scheduledDate < startOfNextWeek) {
      groups.week.items.push(msg);
    } else {
      groups.later.items.push(msg);
    }
  });

  return Object.values(groups).filter(g => g.items.length > 0);
});

// History: sent + failed + cancelled, grouped by conversation
const historyMessages = computed(() => {
  return baseFiltered.value
    .filter(msg => ['sent', 'failed', 'cancelled'].includes(msg.status))
    .sort((a, b) => b.scheduled_at - a.scheduled_at);
});

const historyGrouped = computed(() => {
  const groups = new Map();
  historyMessages.value.forEach(msg => {
    const key = msg.conversation_display || `#${msg.conversation_id}`;
    if (!groups.has(key)) {
      groups.set(key, {
        label: key,
        conversation_id: msg.conversation_id,
        inbox_name: msg.inbox_name,
        items: [],
      });
    }
    groups.get(key).items.push(msg);
  });
  return Array.from(groups.values());
});

const statusCounts = computed(() => ({
  pending: allFollowUps.value.filter(m => m.status === 'pending').length,
  history: allFollowUps.value.filter(m =>
    ['sent', 'failed', 'cancelled'].includes(m.status)
  ).length,
}));

onMounted(async () => {
  isLoading.value = true;
  try {
    await Promise.all([
      store.dispatch('followUps/fetchAll'),
      store.dispatch('followUpTemplates/fetch'),
    ]);
  } catch (error) {
    useAlert(t('FOLLOW_UPS.MESSAGES.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
});

// BUG-FU-02: when this view is kept-alive and the user returns to it after
// cancelling/creating a follow-up from a conversation, re-fetch so the Pending/
// History counts and lists are fresh. Skip the first activation (it coincides
// with onMounted) to avoid a duplicate initial fetch.
const isFirstActivation = ref(true);
onActivated(() => {
  if (isFirstActivation.value) {
    isFirstActivation.value = false;
    return;
  }
  store.dispatch('followUps/fetchAll');
});

// Search
const handleSearch = () => {
  showSearchInput.value = !showSearchInput.value;
  if (showSearchInput.value) {
    nextTick(() => {
      document.getElementById('follow-ups-search')?.focus();
    });
  } else {
    searchQuery.value = '';
  }
};

// Navigation
const goToTemplates = () =>
  router.push(accountScopedRoute('follow_up_templates'));
const goToAutomations = () =>
  router.push(accountScopedRoute('follow_up_automations'));

const goToConversation = conversationId => {
  if (!conversationId) return;
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: conversationId },
  });
};

// Inbox dropdown
const selectInbox = id => {
  selectedInbox.value = id;
  isInboxDropdownOpen.value = false;
};

const selectedInboxLabel = computed(() => {
  if (selectedInbox.value === 'all') return t('FOLLOW_UPS.FILTERS.ALL_INBOXES');
  const inbox = availableInboxes.value.find(i => i.id === selectedInbox.value);
  return inbox?.name ?? t('FOLLOW_UPS.FILTERS.ALL_INBOXES');
});

const handleOutsideClick = e => {
  if (!e.target.closest('[data-inbox-dropdown]')) {
    isInboxDropdownOpen.value = false;
  }
};

onMounted(() => document.addEventListener('click', handleOutsideClick));
onBeforeUnmount(() =>
  document.removeEventListener('click', handleOutsideClick)
);

const toggleConversation = key => {
  if (expandedConversations.value.has(key)) {
    expandedConversations.value.delete(key);
  } else {
    expandedConversations.value.add(key);
  }
};

const isExpanded = key => expandedConversations.value.has(key);

// Template — manual follow-up quick apply.
// BUG-FU05 (audit 2026-04-25): previously used template.content which,
// because of BUG-FU01, was the template NAME, not the message. After
// FU01 is fixed, content is empty for item-based templates; we now
// fetch items and concatenate their text content. Attachments are
// ignored at this stage — users can attach via the sequence builder
// if they need rich messages.
const applyTemplate = async templateId => {
  const template = templates.value.find(item => item.id === templateId);
  if (!template) return;

  editForm.value.title = editForm.value.title || template.name;

  if (template.uses_items) {
    try {
      const items = await store.dispatch(
        'followUpTemplates/fetchItems',
        templateId
      );
      const textParts = (items || [])
        .filter(i => i.item_type === 'text' && i.content)
        .map(i => i.content);
      editForm.value.message = textParts.join('\n\n') || '';
    } catch {
      useAlert(t('FOLLOW_UPS.MESSAGES.LOAD_ERROR'));
    }
  } else {
    editForm.value.message = template.content || '';
  }
};

const formatDate = timestamp => {
  const date = new Date(timestamp * 1000);
  const userLocale = (locale.value || navigator.language || 'en-US').replace(
    '_',
    '-'
  );
  return new Intl.DateTimeFormat(userLocale, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const formatRelativeTime = timestamp => {
  const now = Date.now();
  const diff = timestamp * 1000 - now;
  const absDiff = Math.abs(diff);
  const isPast = diff < 0;

  const minutes = Math.floor(absDiff / 60000);
  const hours = Math.floor(absDiff / 3600000);
  const days = Math.floor(absDiff / 86400000);

  let relative;
  if (minutes < 60) relative = `${minutes}min`;
  else if (hours < 24) relative = `${hours}h`;
  else relative = `${days}d`;

  return isPast ? `${relative} atrás` : `em ${relative}`;
};

const getStatusClass = status => {
  switch (status) {
    case 'pending':
      return 'bg-amber-3 text-amber-11 border border-amber-6';
    case 'sent':
      return 'bg-emerald-3 text-emerald-11 border border-emerald-6';
    case 'failed':
      return 'bg-ruby-3 text-ruby-11 border border-ruby-6';
    case 'cancelled':
      return 'bg-n-alpha-1 text-n-slate-11 border border-n-weak';
    default:
      return 'bg-n-alpha-1 text-n-slate-12 border border-n-weak';
  }
};

const getStatusLabel = status => t(`FOLLOW_UPS.STATUS.${status.toUpperCase()}`);

const getStatusIcon = status => {
  switch (status) {
    case 'sent':
      return 'i-lucide-check-circle-2';
    case 'failed':
      return 'i-lucide-x-circle';
    case 'cancelled':
      return 'i-lucide-ban';
    default:
      return 'i-lucide-clock';
  }
};

const isOverdue = msg => msg.scheduled_at * 1000 < Date.now();

const deleteMessage = async message => {
  deletingIds.value.add(message.id);
  try {
    await store.dispatch('followUps/delete', {
      conversationId: message.conversation_id,
      id: message.id,
    });
    useAlert(t('FOLLOW_UPS.MESSAGES.DELETE_SUCCESS'));
  } catch (error) {
    // BUG-03 (audit 2026-06-16): only PENDING follow-ups are deletable; surface a
    // clear reason if one already transitioned (sent/failed/cancelled).
    useAlert(
      error?.response?.status === 422
        ? t('FOLLOW_UPS.MESSAGES.DELETE_NOT_PENDING')
        : t('FOLLOW_UPS.MESSAGES.ERROR')
    );
  } finally {
    deletingIds.value.delete(message.id);
  }
};

const confirmDelete = async message => {
  messageToDelete.value = message;
  const confirmed = await deleteConfirmationRef.value.showConfirmation();
  if (confirmed) {
    await deleteMessage(message);
  }
  messageToDelete.value = null;
};

const isDeleting = messageId => deletingIds.value.has(messageId);

const isRetrying = messageId => retryingIds.value.has(messageId);

// Re-send a failed follow-up. The backend resumes a partially-delivered
// multi-item chain (audit A1), so the customer never receives an already-sent
// item again. conversation_id is the conversation display_id (serializer).
const handleRetry = async message => {
  retryingIds.value.add(message.id);
  try {
    await store.dispatch('followUps/retrySend', {
      conversationId: message.conversation_id,
      id: message.id,
    });
    useAlert(t('FOLLOW_UPS.MESSAGES.RETRY_SUCCESS'));
  } catch (error) {
    useAlert(t('FOLLOW_UPS.MESSAGES.RETRY_ERROR'));
  } finally {
    retryingIds.value.delete(message.id);
  }
};

const openEditModal = message => {
  messageToEdit.value = message;
  const date = new Date(message.scheduled_at * 1000);
  const localDateTime = new Date(
    date.getTime() - date.getTimezoneOffset() * 60000
  )
    .toISOString()
    .slice(0, 16);
  editForm.value = {
    title: message.title || '',
    message: message.message || '',
    scheduled_at: localDateTime,
    follow_up_template_id: null,
  };
  showEditModal.value = true;
};

const closeEditModal = () => {
  showEditModal.value = false;
  messageToEdit.value = null;
  editForm.value = {
    title: '',
    message: '',
    scheduled_at: '',
    follow_up_template_id: null,
  };
};

const handleEditSubmit = async () => {
  if (!messageToEdit.value) return;
  isUpdating.value = true;
  try {
    const scheduledAt = new Date(editForm.value.scheduled_at).toISOString();
    await store.dispatch('followUps/update', {
      conversationId: messageToEdit.value.conversation_id,
      id: messageToEdit.value.id,
      messageData: {
        title: editForm.value.title,
        content: editForm.value.message,
        scheduled_at: scheduledAt,
      },
    });
    await store.dispatch('followUps/fetchAll');
    useAlert(t('FOLLOW_UPS.MESSAGES.UPDATE_SUCCESS'));
    closeEditModal();
  } catch (error) {
    useAlert(t('FOLLOW_UPS.MESSAGES.ERROR'));
  } finally {
    isUpdating.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-background">
    <!-- Header -->
    <header
      class="relative z-50 flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-n-weak bg-n-background"
    >
      <!-- Left: Icon chip + Title + subtitle -->
      <div class="flex items-center gap-3 min-w-0">
        <div
          class="flex size-9 flex-shrink-0 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand"
        >
          <span class="i-lucide-bell-ring size-4" aria-hidden="true" />
        </div>
        <div class="min-w-0">
          <h1
            class="text-sm font-semibold text-n-slate-12 truncate leading-tight"
          >
            {{ t('FOLLOW_UPS.PAGE.TITLE') }}
          </h1>
          <p
            class="hidden md:block text-xs text-n-slate-10 truncate leading-tight"
          >
            {{ t('FOLLOW_UPS.PAGE.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <!-- Right: Nav buttons (admin only) -->
      <div v-if="isAdmin" class="flex items-center gap-1.5">
        <button
          type="button"
          class="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium border border-n-weak bg-n-background text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
          @click="goToTemplates"
        >
          <span class="i-lucide-file-text size-3.5" />
          {{ t('FOLLOW_UPS.NAV.TEMPLATES') }}
        </button>
        <button
          type="button"
          class="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium border border-n-weak bg-n-background text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
          @click="goToAutomations"
        >
          <span class="i-lucide-zap size-3.5" />
          {{ t('FOLLOW_UPS.NAV.AUTOMATIONS') }}
        </button>
        <!-- NOOVI: CSV import (Gap FU-E — fase-11) -->
        <button
          type="button"
          class="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium border border-n-weak bg-n-background text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
          @click="showImportModal = true"
        >
          <span class="i-lucide-upload size-3.5" />
          {{ t('FOLLOW_UPS.IMPORT.BUTTON') }}
        </button>
      </div>
    </header>

    <!-- NOOVI: modal de import CSV (Gap FU-E — fase-11) -->
    <FollowUpImportModal
      v-model:show="showImportModal"
      @imported="onImported"
    />

    <!-- Toolbar: Pill-switcher + Filters (single compact row) -->
    <div
      class="flex-shrink-0 flex items-center gap-2 h-10 px-3 border-b border-n-weak bg-n-background"
    >
      <!-- Pill-style view switcher -->
      <nav
        class="flex items-center rounded-lg bg-n-alpha-1 p-0.5 flex-shrink-0"
      >
        <button
          type="button"
          class="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-md transition-all duration-150"
          :class="
            activeView === 'pending'
              ? 'bg-n-solid-2 shadow-sm text-n-slate-12'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="activeView = 'pending'"
        >
          <span class="i-lucide-clock size-3" />
          {{ t('FOLLOW_UPS.VIEWS.PENDING') }}
          <span
            v-if="statusCounts.pending > 0"
            class="inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-semibold rounded-full"
            :class="
              activeView === 'pending'
                ? 'bg-n-brand text-white'
                : 'bg-n-alpha-2 text-n-slate-10'
            "
          >
            {{ statusCounts.pending }}
          </span>
        </button>
        <button
          type="button"
          class="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-md transition-all duration-150"
          :class="
            activeView === 'history'
              ? 'bg-n-solid-2 shadow-sm text-n-slate-12'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="activeView = 'history'"
        >
          <span class="i-lucide-history size-3" />
          {{ t('FOLLOW_UPS.VIEWS.HISTORY') }}
          <span
            v-if="statusCounts.history > 0"
            class="inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-semibold rounded-full"
            :class="
              activeView === 'history'
                ? 'bg-n-brand text-white'
                : 'bg-n-alpha-2 text-n-slate-10'
            "
          >
            {{ statusCounts.history }}
          </span>
        </button>
      </nav>

      <div class="w-px h-4 bg-n-weak flex-shrink-0" />

      <!-- Expandable Search -->
      <div class="flex items-center flex-shrink-0">
        <div
          class="flex items-center overflow-hidden transition-all duration-200 ease-out"
          :class="showSearchInput ? 'w-52' : 'w-0'"
        >
          <div
            class="h-7 w-full rounded-md border border-n-weak bg-n-alpha-1 flex items-center overflow-hidden transition-colors focus-within:border-n-blue-7 focus-within:bg-n-solid-2"
          >
            <span
              class="i-lucide-search size-3.5 text-n-slate-9 ml-2 flex-shrink-0"
            />
            <input
              id="follow-ups-search"
              v-model="searchQuery"
              type="search"
              class="w-full bg-transparent border-none outline-none ring-0 m-0 px-2 text-[11px] text-n-slate-12 placeholder:text-n-slate-9 reset-base no-margin"
              :placeholder="t('FOLLOW_UPS.PAGE.SEARCH_PLACEHOLDER')"
              @blur="!searchQuery && (showSearchInput = false)"
            />
          </div>
        </div>
        <NextButton
          variant="ghost"
          color="slate"
          size="xs"
          @click="handleSearch"
        >
          <template #icon>
            <span class="i-lucide-search size-3.5" />
          </template>
        </NextButton>
      </div>

      <!-- Inbox Filter -->
      <div
        v-if="availableInboxes.length > 0"
        class="relative flex-shrink-0"
        data-inbox-dropdown
      >
        <button
          type="button"
          class="flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-[11px] font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 border border-n-weak transition-colors"
          @click="isInboxDropdownOpen = !isInboxDropdownOpen"
        >
          <span class="i-lucide-inbox size-3 text-n-slate-10" />
          <span class="max-w-[120px] truncate">{{ selectedInboxLabel }}</span>
          <span
            class="i-lucide-chevron-down size-3 text-n-slate-10 transition-transform duration-150 flex-shrink-0"
            :class="{ 'rotate-180': isInboxDropdownOpen }"
          />
        </button>
        <Transition
          enter-active-class="transition ease-out duration-100"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition ease-in duration-75"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="isInboxDropdownOpen"
            class="absolute z-50 top-full left-0 mt-1 min-w-[160px] max-w-[220px] rounded-lg bg-n-solid-2 shadow-lg border border-n-weak py-1 origin-top-left"
          >
            <button
              type="button"
              class="w-full px-3 py-1.5 text-left text-[11px] transition-colors"
              :class="
                selectedInbox === 'all'
                  ? 'bg-n-alpha-1 text-n-brand font-semibold'
                  : 'text-n-slate-11 hover:bg-n-alpha-1'
              "
              @click="selectInbox('all')"
            >
              {{ t('FOLLOW_UPS.FILTERS.ALL_INBOXES') }}
            </button>
            <button
              v-for="inbox in availableInboxes"
              :key="inbox.id"
              type="button"
              class="w-full px-3 py-1.5 text-left text-[11px] truncate transition-colors"
              :class="
                selectedInbox === inbox.id
                  ? 'bg-n-alpha-1 text-n-brand font-semibold'
                  : 'text-n-slate-11 hover:bg-n-alpha-1'
              "
              @click="selectInbox(inbox.id)"
            >
              {{ inbox.name }}
            </button>
          </div>
        </Transition>
      </div>

      <!-- Date Filter pills -->
      <div class="flex items-center gap-0.5 flex-shrink-0">
        <button
          v-for="filter in ['all', 'today', 'week', 'month']"
          :key="filter"
          type="button"
          class="px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors"
          :class="
            dateFilter === filter
              ? 'bg-n-brand text-white'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1'
          "
          @click="dateFilter = filter"
        >
          {{ t(`FOLLOW_UPS.FILTERS.DATE_${filter.toUpperCase()}`) }}
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto">
      <!-- Loading -->
      <div
        v-if="isLoading"
        class="flex flex-col items-center justify-center min-h-[400px]"
      >
        <div
          class="w-8 h-8 border-2 border-n-weak border-t-n-brand rounded-full animate-spin mb-3"
        />
        <p class="text-[11px] text-n-slate-10">
          {{ t('FOLLOW_UPS.PAGE.LOADING') }}
        </p>
      </div>

      <!-- PENDING VIEW -->
      <div v-else-if="activeView === 'pending'" class="p-4">
        <!-- Empty pending -->
        <div
          v-if="pendingGrouped.length === 0"
          class="flex flex-col items-center justify-center min-h-[360px] text-center"
        >
          <div
            class="flex size-16 items-center justify-center rounded-2xl bg-emerald-3 text-emerald-11 mb-4"
          >
            <span class="i-lucide-check-circle-2 size-8" />
          </div>
          <h3 class="text-base font-semibold text-n-slate-12 mb-1">
            {{ t('FOLLOW_UPS.VIEWS.PENDING_EMPTY_TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-10 max-w-sm">
            {{ t('FOLLOW_UPS.VIEWS.PENDING_EMPTY_DESC') }}
          </p>
        </div>

        <!-- Grouped pending -->
        <div v-else class="space-y-5">
          <div v-for="group in pendingGrouped" :key="group.key">
            <!-- Group Header -->
            <div class="flex items-center gap-2 mb-2.5">
              <span
                class="size-3.5 flex-shrink-0"
                :class="{
                  'i-lucide-alert-circle text-ruby-10': group.key === 'overdue',
                  'i-lucide-sun text-amber-10': group.key === 'today',
                  'i-lucide-calendar text-n-slate-10': group.key === 'week',
                  'i-lucide-calendar-clock text-n-slate-10':
                    group.key === 'later',
                }"
              />
              <h2
                class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider"
              >
                {{ group.label }}
              </h2>
              <span class="text-[10px] text-n-slate-9">
                ({{ group.items.length }})
              </span>
              <div class="flex-1 h-px bg-n-weak" />
            </div>

            <!-- Cards grid -->
            <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
              <div
                v-for="message in group.items"
                :key="message.id"
                class="group rounded-xl outline outline-1 bg-n-solid-2 transition-colors duration-150 overflow-hidden"
                :class="
                  isOverdue(message)
                    ? 'outline-ruby-6 bg-ruby-2'
                    : 'outline-n-weak hover:outline-n-strong'
                "
              >
                <div class="p-3">
                  <!-- Title + overdue badge -->
                  <div class="flex items-start justify-between gap-2 mb-2">
                    <h3
                      class="text-[11px] font-semibold text-n-slate-12 leading-tight flex-1 min-w-0"
                    >
                      {{ message.title || t('FOLLOW_UPS.LIST.NO_TITLE') }}
                    </h3>
                    <span
                      v-if="isOverdue(message)"
                      class="flex-shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-ruby-3 text-ruby-11 border border-ruby-6 uppercase tracking-wide"
                    >
                      {{ t('FOLLOW_UPS.VIEWS.OVERDUE') }}
                    </span>
                  </div>

                  <!-- Message preview -->
                  <p class="text-[11px] text-n-slate-11 line-clamp-2 mb-2.5">
                    {{ message.message }}
                  </p>

                  <!-- Meta -->
                  <div class="space-y-1">
                    <div
                      class="flex items-center gap-1.5 text-[10px] text-n-slate-10"
                    >
                      <span class="i-lucide-clock size-2.5 flex-shrink-0" />
                      <span>{{ formatDate(message.scheduled_at) }}</span>
                      <span class="text-n-weak">·</span>
                      <span
                        class="font-medium"
                        :class="
                          isOverdue(message) ? 'text-ruby-11' : 'text-n-brand'
                        "
                      >
                        {{ formatRelativeTime(message.scheduled_at) }}
                      </span>
                    </div>
                    <button
                      v-if="message.conversation_id"
                      class="flex items-center gap-1.5 text-[10px] text-n-slate-10 hover:text-n-brand transition-colors w-full text-left"
                      @click="goToConversation(message.conversation_id)"
                    >
                      <span
                        class="i-lucide-message-circle size-2.5 flex-shrink-0"
                      />
                      <span class="truncate font-medium">{{
                        message.conversation_display
                      }}</span>
                      <span
                        class="i-lucide-external-link size-2.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </button>
                    <div
                      class="flex items-center gap-1.5 text-[10px] text-n-slate-10"
                    >
                      <span class="i-lucide-inbox size-2.5 flex-shrink-0" />
                      <span class="truncate">{{ message.inbox_name }}</span>
                    </div>
                  </div>
                </div>

                <!-- Actions footer -->
                <div
                  class="px-2.5 py-1.5 border-t border-n-weak bg-n-background flex gap-1"
                >
                  <button
                    v-if="message.conversation_id"
                    type="button"
                    class="flex items-center justify-center h-6 w-6 rounded-md text-n-brand hover:bg-n-alpha-1 transition-colors"
                    :title="t('FOLLOW_UPS.LIST.VIEW_CONVERSATION')"
                    @click="goToConversation(message.conversation_id)"
                  >
                    <span class="i-lucide-external-link size-3" />
                  </button>
                  <button
                    type="button"
                    class="flex-1 flex items-center justify-center gap-1 h-6 text-[10px] font-medium text-n-slate-11 hover:text-n-slate-12 rounded-md hover:bg-n-alpha-1 transition-colors"
                    @click="openEditModal(message)"
                  >
                    <span class="i-lucide-edit-2 size-3" />
                    {{ t('FOLLOW_UPS.LIST.EDIT') }}
                  </button>
                  <button
                    type="button"
                    class="flex-1 flex items-center justify-center gap-1 h-6 text-[10px] font-medium text-ruby-11 rounded-md hover:bg-ruby-3 transition-colors"
                    :disabled="isDeleting(message.id)"
                    @click="confirmDelete(message)"
                  >
                    <span
                      v-if="isDeleting(message.id)"
                      class="i-lucide-loader-2 size-3 animate-spin"
                    />
                    <span v-else class="i-lucide-trash-2 size-3" />
                    {{
                      isDeleting(message.id)
                        ? t('FOLLOW_UPS.LIST.DELETING')
                        : t('FOLLOW_UPS.LIST.DELETE')
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- HISTORY VIEW -->
      <div v-else-if="activeView === 'history'" class="p-4">
        <!-- Empty history -->
        <div
          v-if="historyGrouped.length === 0"
          class="flex flex-col items-center justify-center min-h-[360px] text-center"
        >
          <div
            class="w-16 h-16 rounded-full bg-n-alpha-1 flex items-center justify-center mb-4"
          >
            <span class="i-lucide-history size-8 text-n-slate-10" />
          </div>
          <h3 class="text-sm font-semibold text-n-slate-12 mb-1">
            {{ t('FOLLOW_UPS.VIEWS.HISTORY_EMPTY_TITLE') }}
          </h3>
          <p class="text-[11px] text-n-slate-10 max-w-xs">
            {{ t('FOLLOW_UPS.VIEWS.HISTORY_EMPTY_DESC') }}
          </p>
        </div>

        <!-- Grouped by conversation -->
        <div v-else class="space-y-1.5">
          <div
            v-for="group in historyGrouped"
            :key="group.label"
            class="border border-n-weak rounded-xl overflow-hidden bg-n-solid-2"
          >
            <!-- Conversation Group Header -->
            <button
              type="button"
              class="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-n-alpha-1 transition-colors text-left"
              @click="toggleConversation(group.label)"
            >
              <div
                class="w-7 h-7 rounded-full bg-n-alpha-1 flex items-center justify-center flex-shrink-0"
              >
                <span class="i-lucide-user size-3.5 text-n-brand" />
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-[11px] font-semibold text-n-slate-12 truncate">
                  {{ group.label }}
                </p>
                <p class="text-[10px] text-n-slate-10 truncate">
                  {{ group.inbox_name }} · {{ group.items.length }}
                  {{ t('FOLLOW_UPS.VIEWS.MESSAGES') }}
                </p>
              </div>
              <div class="flex items-center gap-1.5 flex-shrink-0">
                <span
                  v-if="group.items.filter(i => i.status === 'sent').length > 0"
                  class="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-3 text-emerald-11 border border-emerald-6"
                >
                  {{ group.items.filter(i => i.status === 'sent').length }}
                  {{ t('FOLLOW_UPS.STATUS.SENT') }}
                </span>
                <span
                  v-if="
                    group.items.filter(i => i.status === 'failed').length > 0
                  "
                  class="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-ruby-3 text-ruby-11 border border-ruby-6"
                >
                  {{ group.items.filter(i => i.status === 'failed').length }}
                  {{ t('FOLLOW_UPS.STATUS.FAILED') }}
                </span>
                <button
                  v-if="group.conversation_id"
                  type="button"
                  class="text-n-slate-10 hover:text-n-brand flex items-center p-1 rounded-md hover:bg-n-alpha-1 transition-colors"
                  @click.stop="goToConversation(group.conversation_id)"
                >
                  <span class="i-lucide-external-link size-3" />
                </button>
                <span
                  class="i-lucide-chevron-down size-3.5 text-n-slate-10 transition-transform duration-200"
                  :class="{ 'rotate-180': isExpanded(group.label) }"
                />
              </div>
            </button>

            <!-- Items (collapsible) -->
            <div v-if="isExpanded(group.label)" class="divide-y divide-n-weak">
              <div
                v-for="message in group.items"
                :key="message.id"
                class="flex items-start gap-2.5 px-3 py-2.5 hover:bg-n-alpha-1 transition-colors"
              >
                <div
                  class="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  :class="
                    message.status === 'sent'
                      ? 'bg-emerald-3'
                      : message.status === 'failed'
                        ? 'bg-ruby-3'
                        : 'bg-n-alpha-1'
                  "
                >
                  <span
                    class="size-3"
                    :class="[
                      getStatusIcon(message.status),
                      message.status === 'sent'
                        ? 'text-emerald-11'
                        : message.status === 'failed'
                          ? 'text-ruby-11'
                          : 'text-n-slate-10',
                    ]"
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-0.5">
                    <p
                      class="text-[11px] font-semibold text-n-slate-12 truncate"
                    >
                      {{ message.title || t('FOLLOW_UPS.LIST.NO_TITLE') }}
                    </p>
                    <span
                      class="flex-shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
                      :class="getStatusClass(message.status)"
                    >
                      {{ getStatusLabel(message.status) }}
                    </span>
                  </div>
                  <p class="text-[11px] text-n-slate-11 line-clamp-1 mb-0.5">
                    {{ message.message }}
                  </p>
                  <p class="text-[10px] text-n-slate-10">
                    {{ formatDate(message.scheduled_at) }}
                  </p>
                </div>
                <NextButton
                  v-if="message.status === 'failed'"
                  :label="t('FOLLOW_UPS.LIST.RETRY')"
                  icon="i-lucide-rotate-cw"
                  variant="ghost"
                  color="slate"
                  size="xs"
                  :is-loading="isRetrying(message.id)"
                  :disabled="isRetrying(message.id)"
                  class="flex-shrink-0 self-center"
                  @click="handleRetry(message)"
                />
              </div>
            </div>

            <!-- Collapsed summary -->
            <div
              v-else
              class="px-3 py-1.5 text-[10px] text-n-slate-10 cursor-pointer hover:text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
              @click="toggleConversation(group.label)"
            >
              {{
                t('FOLLOW_UPS.VIEWS.SHOW_MESSAGES', {
                  count: group.items.length,
                })
              }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <ConfirmationModal
      ref="deleteConfirmationRef"
      :title="t('FOLLOW_UPS.DELETE.TITLE')"
      :description="t('FOLLOW_UPS.DELETE.CONFIRM')"
      :confirm-label="t('FOLLOW_UPS.DELETE.CONFIRM_BUTTON')"
      :cancel-label="t('FOLLOW_UPS.DELETE.CANCEL')"
    />

    <!-- Edit Modal -->
    <div
      v-if="showEditModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="closeEditModal"
    >
      <div
        class="bg-n-solid-2 rounded-xl shadow-xl border border-n-weak w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
      >
        <!-- Modal Header -->
        <div
          class="flex items-center justify-between px-4 h-12 border-b border-n-weak"
        >
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('FOLLOW_UPS.FORM.EDIT_TITLE') }}
          </h3>
          <button
            type="button"
            class="text-n-slate-10 hover:text-n-slate-11 p-1 rounded-lg hover:bg-n-alpha-1"
            @click="closeEditModal"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <!-- Modal Body -->
        <form class="p-4 space-y-4" @submit.prevent="handleEditSubmit">
          <!-- Template Selector -->
          <div v-if="templates.length > 0">
            <label class="block text-[11px] font-medium text-n-slate-12 mb-1.5">
              {{ t('FOLLOW_UPS.FORM.TEMPLATE_LABEL') }}
            </label>
            <div class="relative">
              <select
                v-model="editForm.follow_up_template_id"
                class="!mb-0 w-full h-8 pl-3 pr-8 text-[11px] border border-n-weak rounded-lg bg-n-background text-n-slate-12 appearance-none bg-none cursor-pointer"
                @change="applyTemplate(editForm.follow_up_template_id)"
              >
                <option :value="null">
                  {{ t('FOLLOW_UPS.FORM.NO_TEMPLATE') }}
                </option>
                <option
                  v-for="template in templates"
                  :key="template.id"
                  :value="template.id"
                >
                  {{ template.name }}
                </option>
              </select>
              <span
                class="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none i-lucide-chevron-down size-3 text-n-slate-10"
              />
            </div>
          </div>

          <!-- Title -->
          <div>
            <label class="block text-[11px] font-medium text-n-slate-12 mb-1.5">
              {{ t('FOLLOW_UPS.FORM.TITLE_LABEL') }}
            </label>
            <input
              v-model="editForm.title"
              type="text"
              :placeholder="t('FOLLOW_UPS.FORM.TITLE_PLACEHOLDER')"
              class="w-full h-8 px-3 text-[11px] border border-n-weak rounded-lg bg-n-background text-n-slate-12 placeholder:text-n-slate-10 focus:ring-2 focus:ring-n-brand"
            />
          </div>

          <!-- Message -->
          <div>
            <label class="block text-[11px] font-medium text-n-slate-12 mb-1.5">
              {{ t('FOLLOW_UPS.FORM.MESSAGE_LABEL') }}
            </label>
            <textarea
              v-model="editForm.message"
              rows="4"
              :placeholder="t('FOLLOW_UPS.FORM.MESSAGE_PLACEHOLDER')"
              class="w-full px-3 py-2 text-[11px] border border-n-weak rounded-lg bg-n-background text-n-slate-12 placeholder:text-n-slate-10 resize-none focus:ring-2 focus:ring-n-brand"
              required
            />
          </div>

          <!-- Scheduled Time -->
          <div>
            <label class="block text-[11px] font-medium text-n-slate-12 mb-1.5">
              {{ t('FOLLOW_UPS.FORM.SCHEDULE_TIME_LABEL') }}
            </label>
            <input
              v-model="editForm.scheduled_at"
              type="datetime-local"
              class="w-full h-8 px-3 text-[11px] border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:ring-2 focus:ring-n-brand"
              required
            />
          </div>

          <!-- Modal Footer -->
          <div class="flex gap-2.5 pt-2 border-t border-n-weak">
            <NextButton
              type="button"
              variant="faded"
              color="slate"
              size="sm"
              class="flex-1"
              @click="closeEditModal"
            >
              {{ t('FOLLOW_UPS.FORM.CANCEL') }}
            </NextButton>
            <NextButton
              type="submit"
              variant="solid"
              color="blue"
              size="sm"
              class="flex-1"
              :is-loading="isUpdating"
            >
              {{ t('FOLLOW_UPS.FORM.UPDATE') }}
            </NextButton>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
